import { makeStyles, mergeClasses, Popover, PopoverSurface } from '@fluentui/react-components';
import {
    createContext,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { usePreferences } from '../preferences';
import { palette } from '../theme';
import { cssVarsFor } from '../theme/themeVars';
import { WorkItemPreviewCard } from './WorkItemPreviewCard';

// Long enough that a pointer crossing a list doesn't flash cards.
const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 200;
// Above ModalBackdrop, so the card also shows inside dialogs.
const SURFACE_Z_INDEX = 1100;

type PreviewControls = {
    workItemId: number;
    open: boolean;
    // `moveFocus` puts keyboard focus in the card; only for keyboard opens.
    openNow: (moveFocus: boolean) => void;
    close: () => void;
};

const PreviewContext = createContext<PreviewControls | null>(null);

export type WorkItemPreviewProps = {
    workItemId: number;
    title: string;
    workItemType: string;
    // The hover area; a `div` when it wraps block content.
    as?: 'span' | 'div';
    className?: string;
    children: ReactNode;
};

// Hovering the wrapped area opens the work item's card after a short delay;
// clicking its `WorkItemPreviewId` opens it at once.
export const WorkItemPreview: React.FC<WorkItemPreviewProps> = ({
    workItemId,
    title,
    workItemType,
    as: Root = 'span',
    className,
    children,
}) => {
    const classes = useStyles();
    const { resolvedTheme } = usePreferences();
    const [open, setOpen] = useState(false);
    const [focusCard, setFocusCard] = useState(false);
    const [target, setTarget] = useState<HTMLElement | null>(null);
    const timer = useRef<number | null>(null);

    const clearTimer = useCallback(() => {
        if (timer.current !== null) {
            window.clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    useEffect(() => clearTimer, [clearTimer]);

    const setOpenLater = useCallback(
        (next: boolean, delay: number) => {
            clearTimer();
            timer.current = window.setTimeout(() => {
                timer.current = null;
                setOpen(next);
            }, delay);
        },
        [clearTimer],
    );

    const openNow = useCallback(
        (moveFocus: boolean) => {
            clearTimer();
            setFocusCard(moveFocus);
            setOpen(true);
        },
        [clearTimer],
    );

    const close = useCallback(() => {
        clearTimer();
        setOpen(false);
    }, [clearTimer]);

    const onPointerEnter = useCallback(() => {
        if (open) {
            clearTimer();
        } else {
            setFocusCard(false);
            setOpenLater(true, OPEN_DELAY_MS);
        }
    }, [open, clearTimer, setOpenLater]);

    const onPointerLeave = useCallback(() => {
        if (open) {
            setOpenLater(false, CLOSE_DELAY_MS);
        } else {
            clearTimer();
        }
    }, [open, clearTimer, setOpenLater]);

    const controls = useMemo(() => ({ workItemId, open, openNow, close }), [workItemId, open, openNow, close]);

    return (
        <PreviewContext.Provider value={controls}>
            <Root
                ref={setTarget}
                className={className}
                onMouseEnter={onPointerEnter}
                onMouseLeave={onPointerLeave}
                // A row's own click (e.g. opening the day dialog) cancels a pending open.
                onClick={clearTimer}
            >
                {children}
            </Root>
            {open && (
                <Popover
                    open
                    withArrow
                    // A hover or click must not take focus away, e.g. from the search box.
                    unstable_disableAutoFocus={!focusCard}
                    // Beside the item, never over the rows above or below it.
                    positioning={{
                        target,
                        position: 'after',
                        align: 'top',
                        offset: 8,
                        fallbackPositions: ['before-top', 'after-bottom', 'before-bottom'],
                    }}
                    onOpenChange={(_, data) => {
                        if (!data.open) {
                            close();
                        }
                    }}
                >
                    <PopoverSurface
                        className={classes.surface}
                        // The theme variables live inline on the app root, which a portal doesn't inherit.
                        style={cssVarsFor(resolvedTheme) as React.CSSProperties}
                        onMouseEnter={onPointerEnter}
                        onMouseLeave={onPointerLeave}
                        // The card renders in a portal; keep its clicks from reaching the row below.
                        onClick={(e) => e.stopPropagation()}
                    >
                        <WorkItemPreviewCard workItemId={workItemId} title={title} workItemType={workItemType} />
                    </PopoverSurface>
                </Popover>
            )}
        </PreviewContext.Provider>
    );
};

export type WorkItemPreviewIdProps = {
    className?: string;
};

// The `#id` inside a WorkItemPreview: clicking it (or Enter/Space) opens the card
// at once, without triggering the row's own click.
export const WorkItemPreviewId: React.FC<WorkItemPreviewIdProps> = ({ className }) => {
    const classes = useStyles();
    const preview = useContext(PreviewContext);
    if (!preview) {
        throw new Error('WorkItemPreviewId must be used within a WorkItemPreview');
    }
    const { workItemId, open, openNow, close } = preview;

    const onClick = (e: MouseEvent) => {
        e.stopPropagation();
        openNow(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            openNow(true);
        } else if (e.key === 'Escape' && open) {
            // Close the card only, not the dialog around it.
            e.stopPropagation();
            close();
        }
    };

    return (
        <span
            // A span, not a <button>: it also sits inside the search result row's <button>.
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="button"
            tabIndex={0}
            className={mergeClasses(classes.id, className)}
            aria-label={`Preview work item #${workItemId}`}
            aria-expanded={open}
            onClick={onClick}
            onKeyDown={onKeyDown}
        >
            #{workItemId}
        </span>
    );
};

const useStyles = makeStyles({
    surface: {
        zIndex: SURFACE_Z_INDEX,
        padding: 0,
        borderRadius: '12px',
        border: `1px solid ${palette.cardBorder}`,
        boxShadow: palette.softShadowLg,
        backgroundColor: palette.panelBg,
    },
    id: {
        cursor: 'pointer',
        borderRadius: '3px',
        '&:hover': {
            textDecorationLine: 'underline',
            textDecorationStyle: 'dotted',
            textUnderlineOffset: '2px',
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '1px',
        },
    },
});
